import {
  SidebarReorderableList,
  SidebarScrollableContainer,
} from '@affine/core/modules/app-sidebar/views';
import { useI18n } from '@affine/i18n';
import clsx from 'clsx';
import type { ComponentProps, ReactNode, Ref, RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { NavigationPanelFavorites } from '../../desktop/components/navigation-panel';
import {
  NavigationPanelTreeSelectionProvider,
  NavigationPanelTreeSelectionToolbar,
} from '../../desktop/components/navigation-panel/tree';
import {
  NavigationTreeSectionOwner,
  type TreeNavigationEntry,
} from '../../desktop/components/navigation-panel/tree/navigation';
import {
  NavigationPanelRevealRenameCompletionContext,
  type NavigationPanelRevealRenameFinishReason,
} from '../../desktop/components/navigation-panel/tree/rename-intent';
import {
  AnimatedPillSegment,
  type AnimatedPillSegmentOption,
} from '../animation/animated-pill-segment';
import { SidebarDisclosureItem } from '../sidebar-disclosure-item';
import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../sidebar-expandable-section';
import { SidebarSearchField } from '../sidebar-search-field';
import {
  SidebarSearchBranch,
  SidebarTreeSearchProvider,
  useSidebarSearchBranch,
  useSidebarSearchState,
} from '../sidebar-tree-search';
import * as contentStyles from './documents-sidebar-content.css';
import { DocumentsSidebarRecent } from './documents-sidebar-recent';
import {
  organizeSections,
  sidebarExpandableList,
  sidebarExpandableScroll,
  sidebarOrganization,
  sidebarWorkspaceExpandableList,
  workspaceAreaSection,
  workspaceSharedAreaGroup,
} from './index.css';
import { NavigationOverlayPane } from './navigation-overlay-pane';
import {
  SidebarCreateProvider,
  SidebarSearchCreateMenu,
} from './sidebar-create-context';
import {
  type DocumentsSidebarView,
  useDocumentsSidebarView,
  useDocumentsSidebarViewScroll,
} from './use-documents-sidebar-view';

const WORKSPACE_NAVIGATION_PATH = ['workspace'];
const PERSONAL_NAVIGATION_PATH = ['personal'];
const TEAM_NAVIGATION_PATH = ['teamAreas'];
const APPS_NAVIGATION_PATH = ['apps'];
const FAVORITES_VIEW_TEST_ID = 'documents-sidebar-view-favorites';

const EMPTY_NAVIGATION_ENTRIES: readonly TreeNavigationEntry[] = [];

type ExpandableSectionProps = Omit<
  ComponentProps<typeof SidebarExpandableSection>,
  | 'action'
  | 'children'
  | 'className'
  | 'collapsedSummary'
  | 'contentClassName'
  | 'contentPadding'
  | 'headerRef'
  | 'testId'
  | 'title'
>;

type ScrollProps = Omit<
  ComponentProps<typeof SidebarScrollableContainer>,
  'children' | 'className' | 'contentPadding'
>;

interface DocumentsSidebarAreaProps {
  action?: ReactNode;
  children: ReactNode;
  groupClassName?: string;
  listRef?: RefObject<HTMLDivElement | null>;
  scrollProps: ScrollProps;
  sectionClassName?: string;
  sectionContentClassName?: string;
  sectionHeaderRef?: ComponentProps<
    typeof SidebarExpandableSection
  >['headerRef'];
  sectionProps: ExpandableSectionProps;
  title: ReactNode;
}

interface DocumentsSidebarWorkspaceAreaProps extends DocumentsSidebarAreaProps {
  collapsedSummary: ReactNode;
  fillLayout: ComponentProps<typeof SidebarExpandableGroup>['fillLayout'];
}

interface DocumentsSidebarTeamAreaProps extends DocumentsSidebarAreaProps {
  headerHeight: number;
  groupRef: Ref<HTMLDivElement>;
}

interface DocumentsSidebarAppsAreaProps {
  action?: ReactNode;
  children: ReactNode;
  scrollProps: ScrollProps;
  sectionProps: ExpandableSectionProps;
  title: ReactNode;
}

export interface DocumentsSidebarContentProps {
  apps?: DocumentsSidebarAppsAreaProps | undefined;
  footer: ReactNode;
  navigationRevealDocId?: string | undefined;
  navigationRevealRename?: boolean | undefined;
  navigationRevealRequestId?: number | undefined;
  onNavigationRevealRenameFinish: (
    requestId: number,
    reason: NavigationPanelRevealRenameFinishReason
  ) => void;
  personal: DocumentsSidebarAreaProps & {
    fillLayout: ComponentProps<typeof SidebarExpandableGroup>['fillLayout'];
  };
  rootRef: Ref<HTMLDivElement>;
  overlayRootProps?: ComponentProps<typeof NavigationOverlayPane>['rootProps'];
  teamAreas: DocumentsSidebarTeamAreaProps;
  twoPane: boolean;
  workspace: DocumentsSidebarWorkspaceAreaProps;
}

export function DocumentsSidebarContent({
  apps,
  footer,
  navigationRevealDocId,
  navigationRevealRename,
  navigationRevealRequestId,
  onNavigationRevealRenameFinish,
  personal,
  rootRef,
  overlayRootProps,
  teamAreas,
  twoPane,
  workspace,
}: DocumentsSidebarContentProps) {
  const t = useI18n();
  const [query, setQuery] = useState('');
  const [view, showView] = useDocumentsSidebarView(navigationRevealRequestId);
  const viewSwitch = useRef<HTMLDivElement>(null);
  const workspaceView = useRef<HTMLDivElement>(null);
  useDocumentsSidebarViewScroll(view, workspaceView);
  const showWorkspace = useCallback(() => showView('workspace'), [showView]);
  const showFavorites = useCallback(() => showView('favorites'), [showView]);
  const workspaceShown = view === 'workspace';
  const favoritesShown = view === 'favorites';
  const recentShown = view === 'recent';
  const views = useMemo<AnimatedPillSegmentOption<DocumentsSidebarView>[]>(
    () => [
      {
        key: 'workspace',
        label: t['com.streamlain.rootAppSidebar.workspace'](),
        testId: 'documents-sidebar-view-workspace',
      },
      {
        key: 'favorites',
        label: t['com.streamlain.rootAppSidebar.favorites'](),
        testId: FAVORITES_VIEW_TEST_ID,
      },
      {
        key: 'recent',
        label: t['com.streamlain.cmdk.affine.category.affine.recent'](),
        testId: 'documents-sidebar-view-recent',
      },
    ],
    [t]
  );
  const section = { shown: workspaceShown, onShow: showWorkspace };
  return (
    <SidebarCreateProvider>
      <NavigationPanelRevealRenameCompletionContext.Provider
        value={onNavigationRevealRenameFinish}
      >
        <NavigationOverlayPane
          contentClassName={contentStyles.root}
          rootProps={overlayRootProps}
          contentProps={{
            ref: rootRef,
            'data-sidebar-scroll-viewport': 'true',
            'data-navigation-reveal-surface': 'true',
            'data-navigation-reveal-doc-id': navigationRevealDocId,
            'data-navigation-reveal-request-id': navigationRevealRequestId,
            'data-navigation-reveal-rename': navigationRevealRename
              ? 'true'
              : undefined,
          }}
          footer={footer}
          header={
            <>
              <div ref={viewSwitch} className={contentStyles.viewSwitch}>
                <AnimatedPillSegment<DocumentsSidebarView>
                  active={view}
                  ariaLabel={t['com.streamlain.rootAppSidebar.workspace']()}
                  onChange={showView}
                  options={views}
                  fill
                />
              </div>
              <div className={contentStyles.searchSlot}>
                <SidebarSearchField
                  action={
                    <SidebarSearchCreateMenu
                      name={query}
                      onCreate={() => setQuery('')}
                    />
                  }
                  value={query}
                  onChange={setQuery}
                  placeholder={t[
                    'com.streamlain.sidebar.documents.search.placeholder'
                  ]()}
                  ariaLabel={t[
                    'com.streamlain.sidebar.documents.search.placeholder'
                  ]()}
                  clearButtonTestId="documents-sidebar-search-clear"
                />
              </div>
            </>
          }
          testId="global-navigation-docs-panel"
          twoPane={twoPane}
        >
          {/* Each list answers the search on its own; the one put away stays mounted: its areas
              keep offering their create actions, and navigation can still lead into it. */}
          <SidebarTreeSearchProvider query={workspaceShown ? query : ''}>
            <div
              ref={workspaceView}
              className={contentStyles.view}
              data-shown={workspaceShown}
              data-testid="documents-sidebar-workspace-view"
            >
              <DocumentsSidebarSearchEmpty />
              <DocumentsSidebarWorkspaceNavigation workspace={workspace} />
              <DocumentsSidebarSection
                {...section}
                area={personal}
                navigationPath={PERSONAL_NAVIGATION_PATH}
                testId="sidebar-personal"
              >
                <div
                  data-testid="sidebar-personal-organization"
                  className={sidebarOrganization}
                >
                  <NavigationPanelTreeSelectionProvider>
                    <div
                      ref={personal.listRef}
                      data-testid="sidebar-personal-organize"
                      className={organizeSections}
                    >
                      {personal.children}
                    </div>
                    <NavigationPanelTreeSelectionToolbar />
                  </NavigationPanelTreeSelectionProvider>
                </div>
              </DocumentsSidebarSection>
              <DocumentsSidebarSection
                {...section}
                area={teamAreas}
                navigationPath={TEAM_NAVIGATION_PATH}
                groupRef={teamAreas.groupRef}
                testId="sidebar-team-areas"
              >
                <div
                  data-testid="sidebar-organization"
                  className={sidebarOrganization}
                >
                  <NavigationPanelTreeSelectionProvider>
                    <div
                      ref={teamAreas.listRef}
                      data-testid="sidebar-organize"
                      className={organizeSections}
                    >
                      {teamAreas.children}
                    </div>
                    <NavigationPanelTreeSelectionToolbar />
                  </NavigationPanelTreeSelectionProvider>
                </div>
              </DocumentsSidebarSection>

              {apps ? (
                <DocumentsSidebarSection
                  {...section}
                  area={apps}
                  navigationPath={APPS_NAVIGATION_PATH}
                  testId="sidebar-apps"
                >
                  <div
                    data-testid="sidebar-apps-organization"
                    className={sidebarOrganization}
                  >
                    <NavigationPanelTreeSelectionProvider>
                      <div
                        data-testid="sidebar-apps-organize"
                        className={organizeSections}
                      >
                        <SidebarReorderableList
                          className={workspaceSharedAreaGroup}
                          scope="root-sidebar:app-areas"
                        >
                          <div className={workspaceAreaSection}>
                            {apps.children}
                          </div>
                        </SidebarReorderableList>
                      </div>
                      <NavigationPanelTreeSelectionToolbar />
                    </NavigationPanelTreeSelectionProvider>
                  </div>
                </DocumentsSidebarSection>
              ) : null}
            </div>
          </SidebarTreeSearchProvider>
          <SidebarTreeSearchProvider query={favoritesShown ? query : ''}>
            <div
              className={contentStyles.view}
              data-shown={favoritesShown}
              data-testid="documents-sidebar-favorites-view"
            >
              <DocumentsSidebarSearchEmpty />
              <div className={sidebarOrganization}>
                <NavigationPanelTreeSelectionProvider>
                  <div className={organizeSections}>
                    <NavigationPanelFavorites
                      presentation="panel"
                      navigationControl={{
                        triggerRef: viewSwitch,
                        triggerSelector: `[data-testid="${FAVORITES_VIEW_TEST_ID}"]`,
                        open: favoritesShown,
                        onOpen: showFavorites,
                      }}
                    />
                  </div>
                  <NavigationPanelTreeSelectionToolbar />
                </NavigationPanelTreeSelectionProvider>
              </div>
            </div>
          </SidebarTreeSearchProvider>
          <SidebarTreeSearchProvider query={recentShown ? query : ''}>
            <div
              className={contentStyles.view}
              data-shown={recentShown}
              data-testid="documents-sidebar-recent-view"
            >
              <DocumentsSidebarSearchEmpty />
              <DocumentsSidebarRecent shown={recentShown} />
            </div>
          </SidebarTreeSearchProvider>
        </NavigationOverlayPane>
      </NavigationPanelRevealRenameCompletionContext.Provider>
    </SidebarCreateProvider>
  );
}

function DocumentsSidebarSearchEmpty() {
  const t = useI18n();
  const search = useSidebarSearchState();
  return search.active && !search.hasMatches ? (
    <p role="status" className={contentStyles.searchEmpty}>
      {t['com.streamlain.cmdk.no-results']()}
    </p>
  ) : null;
}

function DocumentsSidebarWorkspaceNavigation({
  workspace,
}: {
  workspace: DocumentsSidebarWorkspaceAreaProps;
}) {
  const search = useSidebarSearchState();
  return (
    <div
      className={contentStyles.workspaceNavigation}
      data-sidebar-search-hidden={search.active || undefined}
    >
      <SidebarExpandableGroup
        className={workspace.groupClassName}
        expanded={workspace.sectionProps.expanded}
        fillLayout={workspace.fillLayout}
        placement="top"
      >
        <SidebarExpandableSection
          navigationPath={WORKSPACE_NAVIGATION_PATH}
          className={workspace.sectionClassName}
          collapsedSummary={workspace.collapsedSummary}
          contentPadding="none"
          contentClassName={workspace.sectionContentClassName}
          {...workspace.sectionProps}
          resizable={false}
          testId="sidebar-workspace"
          title={workspace.title}
        >
          <SidebarScrollableContainer
            className={sidebarExpandableScroll}
            contentPadding="none"
            {...workspace.scrollProps}
          >
            <SidebarReorderableList
              ref={workspace.listRef}
              className={clsx(
                sidebarExpandableList,
                sidebarWorkspaceExpandableList
              )}
              scope="root-sidebar:workspace-navigation"
            >
              {workspace.children}
            </SidebarReorderableList>
          </SidebarScrollableContainer>
        </SidebarExpandableSection>
      </SidebarExpandableGroup>
    </div>
  );
}

/** Natural-height regions share one scroll viewport; no area resize state. */
function DocumentsSidebarSection({
  area,
  children,
  navigationPath,
  groupRef,
  shown,
  onShow,
  testId,
}: {
  area: Pick<
    DocumentsSidebarAreaProps,
    'action' | 'sectionHeaderRef' | 'sectionProps' | 'title'
  >;
  children: ReactNode;
  navigationPath: string[];
  groupRef?: Ref<HTMLDivElement>;
  /** The workspace view is the one on screen. */
  shown: boolean;
  onShow: () => void;
  testId: string;
}) {
  const search = useSidebarSearchBranch(area.title);
  const expanded = search.active || area.sectionProps.expanded;
  const navigationHeaderRef = useRef<HTMLDivElement | null>(null);
  const bindHeader = useCallback(
    (element: HTMLDivElement | null) => {
      navigationHeaderRef.current = element;
      const headerRef = area.sectionHeaderRef;
      if (typeof headerRef === 'function') return headerRef(element);
      if (headerRef) headerRef.current = element;
    },
    [area.sectionHeaderRef]
  );
  const setExpanded = useCallback(
    (value: boolean) => {
      if (!search.active) area.sectionProps.onExpandedChange(value);
    },
    [area.sectionProps, search.active]
  );
  const section = (
    <SidebarSearchBranch id={search.id}>
      <div
        className={contentStyles.area}
        ref={groupRef}
        data-sidebar-search-hidden={!search.visible || undefined}
      >
        <SidebarDisclosureItem
          // Area owners register creation actions even before first expansion.
          keepMounted
          hoverActions
          actions={area.action}
          animate={search.animate}
          animateContent={false}
          expanded={expanded}
          onExpandedChange={setExpanded}
          headerRef={bindHeader}
          headerProps={{ 'data-clickable-header': 'true' }}
          regionClassName={contentStyles.region}
          data-expanded={expanded}
          data-placement="top"
          data-testid={testId}
          title={area.title}
        >
          {children}
        </SidebarDisclosureItem>
      </div>
    </SidebarSearchBranch>
  );
  return typeof area.title === 'string' ? (
    <NavigationTreeSectionOwner
      path={navigationPath}
      label={area.title}
      entries={EMPTY_NAVIGATION_ENTRIES}
      headerRef={navigationHeaderRef}
      headerSelector="button[aria-expanded]"
      expanded={shown && expanded}
      onExpand={() => {
        onShow();
        setExpanded(true);
      }}
    >
      {section}
    </NavigationTreeSectionOwner>
  ) : (
    section
  );
}
